import { MdCalendarToday, MdEditCalendar } from "react-icons/md";
import PropTypes from "prop-types";
import { useEffect, useState, useRef } from "react";

const todayISO = new Date().toISOString().substring(0, 10);

const formatDate = (d) =>
  new Date(d).toLocaleDateString("fi-FI", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });

const toDateInputValue = (value) => {
  if (!value) return "";
  return String(value).substring(0, 10);
};

const RentalTimeBar = ({
  startDate,
  endDate,
  billableDays,
  pricePerDay,
  quantity,
  onChangeDates,
  onTogglePanel,
  showPanel,
}) => {
  const hasDates = Boolean(startDate && endDate);
  const totalPrice = billableDays * pricePerDay * (quantity || 1);

  const [hideBar, setHideBar] = useState(false);

  const startRef = useRef(null);
  const endRef = useRef(null);

  // Piilota bar footerin päällä
  useEffect(() => {
    const threshold = 100;

    const onScroll = () => {
      const atBottom =
        window.innerHeight + window.scrollY >=
        document.body.scrollHeight - threshold;

      setHideBar(atBottom);
    };

    window.addEventListener("scroll", onScroll);
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (hideBar) return null;

  const startInputValue = toDateInputValue(startDate);
  const endInputValue = toDateInputValue(endDate);

  return (
    <>
      {/* ====== ALABAR ====== */}
      <div
        className="
          fixed bottom-3 left-1/2 z-50 w-[calc(100%-1.5rem)] max-w-[820px]
          -translate-x-1/2 rounded-[1.5rem]
          border border-[#DDE7E5] bg-white/95 px-4 py-3
          text-[#111827] shadow-[0_18px_50px_rgba(0,0,0,0.25)]
          backdrop-blur-xl
          dark:border-darkborder dark:bg-[#111114]/95 dark:text-white
          sm:px-5
        "
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {/* VASEN */}
          <div className="flex min-w-0 items-start gap-3">
            <div
              className="
                flex h-11 w-11 shrink-0 items-center justify-center rounded-full
                border border-primary/30 bg-primary/10 text-primary
                dark:border-primary/30 dark:bg-primary/5
              "
            >
              <MdCalendarToday className="text-xl" />
            </div>

            {hasDates ? (
              <div className="min-w-0">
                <div className="font-customFont1 text-sm font-extrabold !text-[#111827] dark:!text-white sm:text-base">
                  Vuokra-aika
                </div>

                <div className="font-customFont1 text-sm font-semibold !text-[#374151] dark:!text-[#D1D5DB]">
                  {formatDate(startDate)} – {formatDate(endDate)}
                </div>

                <div className="mt-0.5 font-customFont1 text-xs font-semibold !text-[#4B5563] dark:!text-[#D1D5DB]">
                  Laskutus: {billableDays} vrk / 24 h jaksoissa
                </div>
              </div>
            ) : (
              <div>
                <div className="font-customFont1 text-sm font-extrabold !text-[#111827] dark:!text-white sm:text-base">
                  Valitse vuokra-aika
                </div>

                <div className="font-customFont1 text-xs font-semibold !text-[#4B5563] dark:!text-[#D1D5DB]">
                  Valitse aloitus- ja lopetuspäivä ennen koriin lisäämistä.
                </div>
              </div>
            )}
          </div>

          {/* OIKEA */}
          <div className="flex items-center justify-between gap-4 sm:block sm:text-right">
            <div>
              {hasDates ? (
                <>
                  <div className="font-customFont1 text-2xl font-extrabold !text-[#111827] dark:!text-white">
                    {totalPrice} €
                  </div>

                  <div className="font-customFont1 text-xs font-semibold !text-[#4B5563] dark:!text-[#D1D5DB]">
                    {pricePerDay} € / vrk
                    {billableDays > 1 && ` × ${billableDays}`}
                    {quantity > 1 && ` × ${quantity}`}
                  </div>
                </>
              ) : (
                <div className="font-customFont1 text-lg font-extrabold !text-[#111827] dark:!text-white">
                  —
                </div>
              )}
            </div>

            {hasDates && (
              <button
                type="button"
                onClick={onTogglePanel}
                className="
                  mt-0 inline-flex items-center gap-1 rounded-full border border-primary/40
                  bg-primary/10 px-3 py-1.5 font-customFont1 text-xs font-extrabold
                  !text-[#111827] transition
                  hover:-translate-y-0.5 hover:border-primary hover:bg-primary hover:!text-[#111827]
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                  focus-visible:ring-offset-2 focus-visible:ring-offset-white
                  dark:!text-white dark:hover:!text-[#111827] dark:focus-visible:ring-offset-[#111114]
                  sm:mt-2
                "
              >
                <MdEditCalendar />
                {showPanel ? "Sulje" : "Muuta aikaa"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ====== PIENI KALENTERIPANEELI ====== */}
      {showPanel && hasDates && !hideBar && (
        <div
          className="
            fixed bottom-[112px] left-1/2 z-40 w-[calc(100%-1.5rem)] max-w-[820px]
            -translate-x-1/2 rounded-[1.5rem]
            border border-[#DDE7E5] bg-white/95 px-4 py-4
            text-[#111827] shadow-[0_-10px_35px_rgba(0,0,0,0.18)]
            backdrop-blur-xl
            dark:border-darkborder dark:bg-[#111114]/95 dark:text-white
            sm:px-5
          "
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* ALKAA */}
            <div className="relative">
              <label
                htmlFor="rental-start-date"
                className="mb-1 block font-customFont1 text-sm font-extrabold !text-[#111827] dark:!text-white"
              >
                Alkaa
              </label>

              <input
                id="rental-start-date"
                ref={startRef}
                type="date"
                min={todayISO}
                value={startInputValue}
                onChange={(e) =>
                  onChangeDates({
                    startDate: e.target.value,
                    endDate,
                  })
                }
                className="
                  w-full rounded-2xl border border-[#DDE7E5] bg-[#F8FAFC]
                  px-4 py-3 pr-12 font-customFont1 text-base font-semibold
                  text-[#111827] outline-none transition
                  focus:border-primary focus:ring-2 focus:ring-primary/30
                  dark:border-darkborder dark:bg-darksurface dark:text-white
                "
              />

              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() =>
                  startRef.current?.showPicker
                    ? startRef.current.showPicker()
                    : startRef.current?.focus()
                }
                className="
                  absolute right-3 top-[38px] flex h-9 w-9 items-center justify-center
                  rounded-full text-lg transition hover:bg-primary/10
                "
                aria-label="Avaa aloituspäivän kalenteri"
              >
                📅
              </button>
            </div>

            {/* LOPPUU */}
            <div className="relative">
              <label
                htmlFor="rental-end-date"
                className="mb-1 block font-customFont1 text-sm font-extrabold !text-[#111827] dark:!text-white"
              >
                Loppuu
              </label>

              <input
                id="rental-end-date"
                ref={endRef}
                type="date"
                value={endInputValue}
                min={startInputValue}
                onChange={(e) => {
                  const nextEnd = e.target.value;

                  if (nextEnd < startInputValue) return;

                  onChangeDates({
                    startDate,
                    endDate: nextEnd,
                  });
                }}
                className="
                  w-full rounded-2xl border border-[#DDE7E5] bg-[#F8FAFC]
                  px-4 py-3 pr-12 font-customFont1 text-base font-semibold
                  text-[#111827] outline-none transition
                  focus:border-primary focus:ring-2 focus:ring-primary/30
                  dark:border-darkborder dark:bg-darksurface dark:text-white
                "
              />

              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() =>
                  endRef.current?.showPicker
                    ? endRef.current.showPicker()
                    : endRef.current?.focus()
                }
                className="
                  absolute right-3 top-[38px] flex h-9 w-9 items-center justify-center
                  rounded-full text-lg transition hover:bg-primary/10
                "
                aria-label="Avaa lopetuspäivän kalenteri"
              >
                📅
              </button>
            </div>
          </div>

          <div
            className="
              mt-4 rounded-2xl border border-primary/30 bg-primary/10 p-3
              text-center font-customFont1 text-sm font-bold
              !text-[#111827]
              dark:border-primary/30 dark:bg-primary/5 dark:!text-white
            "
          >
            Vuokraus laskutetaan 24 h jaksoissa.
          </div>
        </div>
      )}
    </>
  );
};

RentalTimeBar.propTypes = {
  startDate: PropTypes.any,
  endDate: PropTypes.any,
  billableDays: PropTypes.number.isRequired,
  pricePerDay: PropTypes.number.isRequired,
  quantity: PropTypes.number,
  onChangeDates: PropTypes.func.isRequired,
  onTogglePanel: PropTypes.func.isRequired,
  showPanel: PropTypes.bool.isRequired,
};

export default RentalTimeBar;